<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { useCropStore } from '@shared';
import { ParcelListElementStatus } from '../../enums/parcel-list-element-status.enum';
import { type ParcelItem } from '../../models/parcel-item.model';
import { useColorMapStore, findColorByValue } from '../../../colormap';
import { storeToRefs } from 'pinia';

const props = defineProps({
  parcel: {
    type: Object as PropType<ParcelItem>,
    required: true
  },
  status: {
    type: String as PropType<ParcelListElementStatus>,
    required: true
  },
  removable: {
    type: Boolean,
    default: true
  },
  showHeightmapCoverage: {
    type: Boolean,
    default: false
  },
  inclineLayersBlocked: {
    type: Boolean,
    default: false
  },
  blockWithoutValidZoning: {
    type: Boolean,
    default: false
  }
});

defineEmits(['selectParcel', 'unselectParcel']);

const cropStore = useCropStore();
const colorMapStore = useColorMapStore();
const { heightmapColorMap } = storeToRefs(colorMapStore);

const getHeightmapColor = computed(() => {
  if (
    !props.parcel.heightmapCoverage ||
    !heightmapColorMap.value ||
    !heightmapColorMap.value.breaks ||
    !heightmapColorMap.value.colors
  ) {
    return;
  }

  return findColorByValue(
    props.parcel.heightmapCoverage,
    heightmapColorMap.value.breaks,
    heightmapColorMap.value.colors
  );
});

const getCropNameByCode = computed(() => cropStore.crops.find((crop) => crop.code === props.parcel.currentCrop)?.name);
</script>

<template>
  <v-card
    elevation="2"
    class="d-flex my-2 mx-2 flex-column">
    <div class="d-flex my-1 mx-2 align-center">
      <div
        v-if="parcel.svg"
        class="svg-container"
        v-html="parcel.svg"></div>
      <div
        v-else
        class="svg-container">
        <v-icon
          class="ma-2"
          size="36px"
          >mdi-folder</v-icon
        >
      </div>
      <div class="parcel-element__title d-flex flex-column flex-grow-1 ml-1">
        <h4
          class="text-center"
          :class="{ 'text-grey': parcel.archived }">
          <v-tooltip
            activator="parent"
            location="top">
            {{ parcel.name }} </v-tooltip
          >{{ parcel.name }}
        </h4>
      </div>
      <div class="mx-2">
        <template v-if="status === ParcelListElementStatus.SELECTABLE">
          <template v-if="parcel.archived">
            <v-tooltip
              activator="parent"
              location="top"
              :text="$t('tooltip.parcelArchived')" />
            <v-icon icon="mdi-archive-outline" />
          </template>
          <template v-else>
            <v-tooltip
              v-if="!parcel.hasValidZoning && blockWithoutValidZoning"
              activator="parent"
              location="top"
              :text="$t('tooltip.notValidZoning')" />
            <v-tooltip
              v-if="parcel.hasInclineLayers && inclineLayersBlocked"
              activator="parent"
              location="top"
              :text="$t('tooltip.hasInclineLayers')" />
            <v-tooltip
              v-else-if="parcel.blocked"
              activator="parent"
              location="top"
              :text="$t('tooltip.parcelBlocked')" />
            <v-icon
              v-if="
                parcel.blocked ||
                (parcel.hasInclineLayers && inclineLayersBlocked) ||
                (!parcel.hasValidZoning && blockWithoutValidZoning)
              "
              icon="mdi-lock-outline" />
            <v-icon
              v-else
              @click="$emit('selectParcel')"
              color="primary-lighter"
              size="40px"
              >mdi-plus-circle-outline</v-icon
            >
          </template>
        </template>

        <v-icon
          v-if="status === ParcelListElementStatus.ADDED"
          size="40px"
          color="primary"
          >mdi-check-circle-outline</v-icon
        >
        <v-icon
          v-if="removable && status === ParcelListElementStatus.SELECTED"
          @click="$emit('unselectParcel')"
          size="30px"
          color="red">
          mdi-close
        </v-icon>
      </div>
    </div>
    <div
      v-if="showHeightmapCoverage && parcel.heightmapCoverage"
      :style="{ backgroundColor: getHeightmapColor }"
      class="d-flex justify-space-around text-no-wrap">
      <v-col class="parcel-element-tag">
        <span>{{ $t('parcel.dialog.newParcelGroup.selectedParcels.currentCoverage') }}:</span>&nbsp;
        <span>{{ parcel.heightmapCoverage }}%</span>
      </v-col>
    </div>
    <div class="bg-grey-lighten-2 d-flex justify-space-around text-no-wrap">
      <v-col class="parcel-element-tag">
        <v-tooltip
          activator="parent"
          location="top">
          {{ $t('parcel.dialog.newParcelGroup.area') }}: {{ parcel.area }}
          {{ $t('parcel.dialog.newParcelGroup.hectare') }} </v-tooltip
        >{{ parcel.area }} {{ $t('parcel.dialog.newParcelGroup.hectare') }}
      </v-col>
      <v-col
        class="parcel-element-tag"
        v-if="parcel.currentCrop">
        <v-tooltip
          activator="parent"
          location="top">
          {{ $t('parcel.dialog.newParcelGroup.currentCrop') }}: {{ getCropNameByCode }}
        </v-tooltip>
        {{ getCropNameByCode }}
      </v-col>
      <v-col
        v-if="status === ParcelListElementStatus.SELECTED && parcel.farmName"
        class="parcel-element-tag">
        <v-tooltip
          activator="parent"
          location="top">
          {{ $t('parcel.dialog.newParcelGroup.farmName') }}: {{ parcel.farmName }}
        </v-tooltip>
        {{ parcel.farmName }}
      </v-col>
    </div>
  </v-card>
</template>
